import { Avatar, Group, Stack, Text, UnstyledButton } from '@cowbell/ui/core';
import { useMooConfig } from '../../provider';
import type { Agent } from '../../types';
import { CheckIcon } from './icons';
import styles from './ShellDrawer.module.css';

export interface AgentRowProps {
  readonly agent: Agent;
  readonly isSelected: boolean;
  readonly onSelect: () => void;
}

/** Single agent entry in the drawer's AGENTS section. Click anywhere on the
 *  row to select; selected state gets a subtle background tint.
 *
 *  Avatar rendering goes through the host's `renderAgentAvatar` slot when
 *  configured (V1's agent.icon is a sprite key, not a URL — only the host
 *  knows how to resolve it). Falls back to the agent's initial when the
 *  slot returns null or is not configured. */
export function AgentRow({ agent, isSelected, onSelect }: AgentRowProps) {
  const { renderAgentAvatar } = useMooConfig();
  const cls = isSelected ? `${styles.row} ${styles.rowSelected}` : styles.row;
  const hostAvatar = renderAgentAvatar?.(agent, { size: 'md' });
  return (
    <UnstyledButton
      onClick={onSelect}
      aria-current={isSelected ? 'true' : undefined}
      className={cls}
    >
      <Group wrap="nowrap" align="center" gap="sm">
        {hostAvatar ?? (
          <Avatar src={agent.iconUrl} radius="xl" size="md">
            {agent.name.charAt(0)}
          </Avatar>
        )}
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Text className={styles.rowName} truncate>
            {agent.name}
          </Text>
          {agent.description ? (
            <Text className={styles.rowDescription} lineClamp={1}>
              {agent.description}
            </Text>
          ) : null}
        </Stack>
        {isSelected ? (
          <span className={styles.rowCheck} aria-hidden>
            <CheckIcon />
          </span>
        ) : null}
      </Group>
    </UnstyledButton>
  );
}
